import { ElMessageBox } from 'element-plus';
import type { Action, MessageBoxState } from 'element-plus';

/**
 * ElMessageBox 通用选项
 */
export interface MessageBoxOptions {
  /** 标题 */
  title?: string;
  /** 确认按钮文本 */
  confirmButtonText?: string;
  /** 取消按钮文本 */
  cancelButtonText?: string;
  /** 消息类型 */
  type?: 'success' | 'info' | 'warning' | 'error';
  /** 是否区分取消与关闭（符合 Windows 习惯：确认在左，取消在右） */
  distinguishCancelAndClose?: boolean;
  /** 自定义 CSS 类名，默认 'pb-msgbox'，用于统一定制弹窗样式 */
  customClass?: string;
  /** 关闭前的回调，返回 false 可阻止关闭 */
  beforeClose?: (action: Action, instance: MessageBoxState, done: () => void) => void;
}

/**
 * 确认弹窗选项
 */
export interface ConfirmOptions extends MessageBoxOptions {
  /** 取消按钮的回调 */
  cancelCallback?: () => void;
}

/**
 * 输入弹窗选项
 */
export interface PromptOptions extends MessageBoxOptions {
  /** 输入框占位文本 */
  inputPlaceholder?: string;
  /** 输入框默认值 */
  inputValue?: string;
  /** 输入框类型 */
  inputType?: 'text' | 'password' | 'textarea';
  /** 输入验证函数，返回 true 表示通过，返回字符串表示错误信息 */
  inputValidator?: (value: string) => boolean | string;
  /** 输入框错误提示 */
  inputErrorMessage?: string;
}

/**
 * 全局消息弹窗管理类（单例）
 *
 * 对 Element Plus 的 ElMessageBox 进行封装，提供统一的弹窗调用方式。
 * 所有弹窗方法都返回 Promise，支持 async/await 风格调用。
 *
 * @example
 * ```ts
 * import { messageBoxManager } from '@/managers';
 *
 * // 提示弹窗
 * await messageBoxManager.alert('操作成功');
 *
 * // 确认弹窗
 * const confirmed = await messageBoxManager.confirm('确定删除吗？');
 * if (confirmed) { ... }
 *
 * // 输入弹窗
 * const name = await messageBoxManager.prompt('请输入名称');
 * if (name) { ... }
 * ```
 */
class MessageBoxManager {
  private static instance: MessageBoxManager | null = null;

  private constructor() {}

  /**
   * 获取单例实例
   */
  static getInstance(): MessageBoxManager {
    if (!MessageBoxManager.instance) {
      MessageBoxManager.instance = new MessageBoxManager();
    }
    return MessageBoxManager.instance;
  }

  /**
   * 提示弹窗（仅有确认按钮）
   *
   * @param message - 提示消息内容
   * @param title   - 弹窗标题，默认 '提示'
   * @param options - 额外选项
   */
  async alert(message: string, title?: string, options?: MessageBoxOptions): Promise<void> {
    try {
      await ElMessageBox.alert(message, title ?? '提示', {
        confirmButtonText: options?.confirmButtonText ?? '确定',
        type: options?.type ?? 'info',
        customClass: options?.customClass ?? 'pb-msgbox',
        distinguishCancelAndClose: options?.distinguishCancelAndClose ?? true,
        beforeClose: options?.beforeClose
      });
    } catch {
      // 用户关闭弹窗，静默处理
    }
  }

  /**
   * 确认弹窗（有确认和取消两个按钮）
   *
   * @param message - 提示消息内容
   * @param title   - 弹窗标题，默认 '提示'
   * @param options - 额外选项
   * @returns 确认返回 true，取消或关闭返回 false
   */
  async confirm(message: string, title?: string, options?: ConfirmOptions): Promise<boolean> {
    try {
      await ElMessageBox.confirm(message, title ?? '提示', {
        confirmButtonText: options?.confirmButtonText ?? '确定',
        cancelButtonText: options?.cancelButtonText ?? '取消',
        type: options?.type ?? 'warning',
        customClass: options?.customClass ?? 'pb-msgbox',
        distinguishCancelAndClose: options?.distinguishCancelAndClose ?? true,
        beforeClose: options?.beforeClose
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 输入弹窗（带输入框）
   *
   * @param message - 提示消息内容
   * @param title   - 弹窗标题，默认 '请输入'
   * @param options - 额外选项
   * @returns 确认返回输入值，取消或关闭返回 null
   */
  async prompt(message: string, title?: string, options?: PromptOptions): Promise<string | null> {
    try {
      const { value } = await ElMessageBox.prompt(message, title ?? '请输入', {
        confirmButtonText: options?.confirmButtonText ?? '确定',
        cancelButtonText: options?.cancelButtonText ?? '取消',
        customClass: options?.customClass ?? 'pb-msgbox',
        type: options?.type,
        inputPlaceholder: options?.inputPlaceholder,
        inputValue: options?.inputValue,
        inputType: options?.inputType,
        inputValidator: options?.inputValidator,
        inputErrorMessage: options?.inputErrorMessage,
        distinguishCancelAndClose: options?.distinguishCancelAndClose ?? true,
        beforeClose: options?.beforeClose
      });
      return value ?? null;
    } catch {
      return null;
    }
  }

  /**
   * 错误提示弹窗
   */
  async error(
    message: string,
    title?: string,
    options?: Omit<MessageBoxOptions, 'type'>
  ): Promise<void> {
    return this.alert(message, title ?? '错误', { ...options, type: 'error' });
  }

  /**
   * 成功提示弹窗
   */
  async success(
    message: string,
    title?: string,
    options?: Omit<MessageBoxOptions, 'type'>
  ): Promise<void> {
    return this.alert(message, title ?? '成功', { ...options, type: 'success' });
  }

  /**
   * 警告提示弹窗
   */
  async warning(
    message: string,
    title?: string,
    options?: Omit<MessageBoxOptions, 'type'>
  ): Promise<void> {
    return this.alert(message, title ?? '警告', { ...options, type: 'warning' });
  }

  /**
   * 危险操作确认弹窗（确认按钮为红色，类型为 error）
   *
   * @param message - 提示消息内容
   * @param title   - 弹窗标题，默认 '危险操作'
   * @param options - 额外选项
   */
  async dangerConfirm(message: string, title?: string, options?: ConfirmOptions): Promise<boolean> {
    return this.confirm(message, title ?? '危险操作', {
      ...options,
      type: 'error',
      confirmButtonText: options?.confirmButtonText ?? '确定删除'
    });
  }
}

/** 全局消息弹窗管理单例 */
export const messageBoxManager = MessageBoxManager.getInstance();
